// Item detail drawer — the trust centerpiece (GET /items/{item_pk})

import React from "react";
import { Badge, ComingSoon, ValueTierDisplay, useDialogFocus } from "./atoms.jsx";
import { CONDITION_LABEL, KARAT_PURITY, REASON_CAT_TONE, REASON_CODES } from "./data.jsx";
import { DealTierBadge, SaveBtn, ThumbPh, goldWeightText, money, moneyC, shippingLine } from "./feed.jsx";
import { I } from "./icons.jsx";
import { weightBasisDescription, weightBasisLabel } from "./weightBasis.jsx";
import "./detail.css";

const STATUS_META = {
  priced: { tone:"priced", label:"Priced", icon:<I.check size={15}/>, txt:"Successfully valued against live gold spot." },
  priced_review_required: { tone:"review", label:"Priced — review required", icon:<I.eye size={15}/>, txt:"Valued, but flagged for human review. Treat the estimate with caution." },
  skipped: { tone:"skipped", label:"Not priced", icon:<I.x size={15}/>, txt:"Could not be valued — see reasons below." },
  skipped_policy: { tone:"skipped", label:"Skipped by policy", icon:<I.x size={15}/>, txt:"Excluded from pricing by a material/authenticity policy gate." },
};

const sellerLabel = (item) => item.seller ? `@${item.seller}` : "\u2014";
const sellerFeedbackLabel = (item) => {
  const parts = [];
  if (item.sellerFeedbackPercent != null) {
    const n = Number(item.sellerFeedbackPercent);
    parts.push(Number.isFinite(n) ? `${n.toFixed(1)}%` : `${item.sellerFeedbackPercent}%`);
  }
  if (item.sellerFeedbackScore != null) {
    parts.push(Number(item.sellerFeedbackScore).toLocaleString());
  }
  return parts.length ? parts.join(" \u00b7 ") : "\u2014";
};

const ReasonCode = ({ code }) => {
  const rc = REASON_CODES[code]; if (!rc) return null;
  const tone = REASON_CAT_TONE[rc.category] || "neutral";
  const color = { live:"var(--live)", gold:"var(--accent)", hot:"var(--hot)", info:"var(--info)", neutral:"var(--muted-2)" }[tone];
  return (
    <div className="reason">
      <span className="rc-dot" style={{background: color}}/>
      <div className="rc-body">
        <div className="rc-label">{rc.label} <span className="rc-cat">{rc.category.replace("_"," ")}</span></div>
        <div className="rc-tip">{rc.tooltip}</div>
      </div>
    </div>
  );
};

const FeedbackForm = ({ item, signedIn, onComingSoon }) => {
  return (
    <ComingSoon
      variant="inline"
      feature="Feedback"
      detail="Ground-truth reports need accounts, so feedback is paused for the anonymous MVP."
      onAction={() => onComingSoon?.("Feedback", "Ground-truth reports need accounts, so feedback is paused for the anonymous MVP.")}
      actionLabel="Coming soon"
    />
  );
  const [rating, setRating] = React.useState(0);
  const [bought, setBought] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  if (!signedIn) {
    return (
      <div className="anon-gate">
        <I.spark size={20} style={{color:"var(--accent-deep)"}}/>
        <div className="ag-txt">
          <h5>Help train the estimates</h5>
          <p>Sign in to report the real karat, weight, and what you paid. Your feedback sharpens future valuations.</p>
        </div>
        <button className="btn accent sm">Sign in</button>
      </div>
    );
  }
  if (sent) {
    return <div className="status-banner priced" style={{marginBottom:0}}><I.check size={15} className="sb-ic"/><span className="sb-txt"><b>Thanks!</b> Your ground-truth report was recorded.</span></div>;
  }
  return (
    <div className="fb-form">
      <div className="fb-grid">
        <div><label className="lbl">Actual karat</label><input className="input mono" placeholder={item.karat ? `${item.karat}K` : "e.g. 14K"}/></div>
        <div><label className="lbl">Actual total weight</label><input className="input mono" placeholder={item.totalWeight ? `${item.totalWeight} g` : "grams"}/></div>
        <div><label className="lbl">Purchase price</label><input className="input mono" placeholder="$0.00"/></div>
        <div>
          <label className="lbl">Did you buy it?</label>
          <div className="seg" style={{width:"fit-content"}}>
            <button className={bought ? "active" : ""} onClick={() => setBought(true)}>Yes</button>
            <button className={!bought ? "active" : ""} onClick={() => setBought(false)}>No</button>
          </div>
        </div>
      </div>
      <label className="lbl">Estimate accuracy</label>
      <div className="fb-stars" style={{marginBottom:10}}>
        {[1,2,3,4,5].map(n => (
          <span key={n} className={`st ${n <= rating ? "on" : ""}`} onClick={() => setRating(n)}><I.star size={18}/></span>
        ))}
      </div>
      <textarea className="input" style={{height:54, padding:"8px 10px", resize:"none"}} placeholder="Notes (optional) — hallmark, hollowness, packaging…"/>
      <div style={{display:"flex", justifyContent:"flex-end", marginTop:10}}>
        <button className="btn accent sm" onClick={() => setSent(true)}><I.check size={13}/> Submit feedback</button>
      </div>
    </div>
  );
};

const labelFromValue = (value) => {
  if (!value) return null;
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
};

const conditionLabel = (value) => CONDITION_LABEL[value] || labelFromValue(value);

const getWeightBasisLabel = (value) => {
  return weightBasisLabel(value) || labelFromValue(value) || "Unknown";
};

const getWeightBasisDescription = (item) => {
  const imported = weightBasisDescription(item);
  if (imported) return imported;
  const basis = item.weightBasis || "none";
  if (basis === "text") {
    return `Listing text states the metal weight directly. Confidence: ${item.weightConfidence}.`;
  }
  if (basis === "image") {
    return `No stated weight - gold weight inferred from image-scale analysis of the photos. Confidence: ${item.weightConfidence}. Treat as an estimate.`;
  }
  if (basis === "none") {
    return "No weight could be determined from the listing text or photos. Item cannot be priced until a weight is known.";
  }
  return `${getWeightBasisLabel(basis)} (${basis})`;
};

const DetailDrawer = ({ item, onClose, onOpen, signedIn, onSave, related, onComingSoon, restoreFocusTo }) => {
  const [activeImg, setActiveImg] = React.useState(0);
  const titleId = React.useId();
  const drawerRef = useDialogFocus({ enabled: Boolean(item), onClose, restoreFocusTo });
  const galleryImages = React.useMemo(() => {
    if (!item) return [];
    return Array.from(new Set([item.imageUrl, ...(item.imageUrls || [])].filter(Boolean)));
  }, [item && item.id, item && item.imageUrl, item && item.imageUrls]);
  React.useEffect(() => { setActiveImg(0); }, [item && item.id]);
  if (!item) return null;
  const priced = item.meltValue != null;
  const below = item.ratio != null && item.ratio < 1.0;
  const sm = STATUS_META[item.pricingStatus] || STATUS_META.skipped;
  const purityPct = item.karat ? (KARAT_PURITY[item.karat] * 100).toFixed(2) : null;
  const activeImage = galleryImages[activeImg] || galleryImages[0] || null;
  const weightBasisName = getWeightBasisLabel(item.weightBasis);
  const weightBasisText = getWeightBasisDescription(item);

  return (
    <>
      <div className="scrim" onClick={onClose}/>
      <div
        className="detail-drawer"
        data-testid="detail-drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="detail-head">
          <div className="dh-left">
            <button className="btn icon sm ghost" type="button" onClick={onClose} aria-label="Back to feed"><I.chevR size={15}/></button>
            <span className="crumb">eBay #{item.itemId}</span>
            <DealTierBadge tier={item.tier}/>
            {item.coverage && <Badge tone="outline">{item.coverage}</Badge>}
          </div>
          <div style={{display:"flex", gap:8}}>
            <SaveBtn item={item} signedIn={signedIn} onSave={onSave}/>
            <button className="btn icon sm ghost" type="button" onClick={onClose} aria-label="Close item details"><I.x size={15}/></button>
          </div>
        </div>

        <div className="detail-body">
          {/* gallery */}
          <div className="gallery">
            <div className="gallery-main"><ThumbPh item={item} src={activeImage} alt={`${item.title} photo ${activeImg + 1}`}/></div>
            <div className="gallery-thumbs">
              {(galleryImages.length ? galleryImages : [null]).map((src, i) => (
                <div key={src || i} className={`gt ${i === activeImg ? "active" : ""}`} onClick={() => setActiveImg(i)}><ThumbPh item={item} src={src} alt={`${item.title} thumbnail ${i + 1}`}/></div>
              ))}
            </div>
          </div>

          <div className="d-title">
            <h2 id={titleId}>{item.title}</h2>
            <div className="d-tags">
              <Badge tone="neutral">{item.itemType}</Badge>
              {item.karat && <Badge tone="gold">{item.karat}K {item.isSolidGold ? "solid" : ""}</Badge>}
              <Badge tone="neutral">{conditionLabel(item.condition)}</Badge>
              {item.authenticityGuarantee && <Badge tone="info" icon={<I.check size={9}/>}>eBay Authenticity Guarantee</Badge>}
              {item.requiresReview && <Badge tone="hot" icon={<I.eye size={9}/>}>Review required</Badge>}
            </div>
          </div>

          {/* hero valuation */}
          <div className={`hero-band ${below ? "below" : ""}`}>
            <div className="hero-ratio">
              <span className="hr-k">Price-to-melt ratio</span>
              <span className="hr-v">{item.ratio != null ? `${item.ratio.toFixed(2)}×` : "N/A"}</span>
              <span className="hr-sub">{item.ratio == null ? "Weight unknown — cannot price" : below ? "Below the value of its gold" : "Above raw melt value"}</span>
            </div>
            <div className="hero-vrule"/>
            <div className="hero-cell">
              <div className="hc-k">All-in price</div>
              <div className="hc-v">{money(item.allIn)}</div>
              <div className="hc-sub">{shippingLine(item, { freeLabel: "free shipping" })}</div>
            </div>
            <div className="hero-cell">
              <div className="hc-k">Value tier</div>
              <div className="hc-v"><ValueTierDisplay tier={item.valueTier} variant="hero"/></div>
              <div className="hc-sub">{item.valueTier ? "based on price vs. melt" : "not tiered"}</div>
            </div>
          </div>

          {/* valuation breakdown */}
          <div className="d-sect">
            <h3>Valuation breakdown</h3>
            <div className="kv-grid">
              <div className="kv"><span className="k">Est. gold melt value</span><span className="v gold">{money(item.meltValue)}</span></div>
              <div className="kv"><span className="k">Gold spot used</span><span className="v">{item.spotPerGram != null ? `${moneyC(item.spotPerGram)}/g` : "N/A"}</span></div>
              <div className="kv"><span className="k">Gold weight est.</span><span className="v" data-testid="detail-gold-weight-value">{item.goldWeight != null ? goldWeightText(item, { spaced: true }) : "unknown"}</span></div>
              <div className="kv"><span className="k">Total weight</span><span className="v">{item.totalWeight != null ? `${item.totalWeight} g` : "unknown"}</span></div>
              <div className="kv"><span className="k">Karat / purity</span><span className="v">{item.karat ? `${item.karat}K · ${purityPct}%` : "unknown"}</span></div>
              <div className="kv"><span className="k">Weight basis</span><span className="v">{weightBasisName} <span className="muted">({item.weightBasis})</span></span></div>
            </div>
          </div>

          {/* honesty panel */}
          <div className="d-sect">
            <h3>Why this verdict <Badge tone={item.overallConfidence === "high" ? "live" : item.overallConfidence === "medium" ? "gold" : "hot"}>{item.overallConfidence} confidence</Badge></h3>
            <div className={`status-banner ${sm.tone}`}>
              <span className="sb-ic">{sm.icon}</span>
              <span className="sb-txt"><b>{sm.label}.</b> {sm.txt}</span>
            </div>
            <div>
              {item.reasonCodes.map(c => <ReasonCode key={c} code={c}/>)}
            </div>
          </div>

          {/* weight evidence */}
          <div className="d-sect">
            <h3>How we estimated the weight</h3>
            <div className="evidence">
              <div className="evidence-row">
                <div className="er-k" data-testid="weight-basis-title"><I.scale size={12}/> Weight basis {item.weightRequiresReview && <Badge tone="hot">needs review</Badge>}</div>
                <div className="er-quote" data-testid="weight-basis-value">{weightBasisText}</div>
              </div>
              {item.weightBasis !== "none" && (
                <div className="evidence-row">
                  <div className="er-k"><I.tag size={12}/> Listing excerpt</div>
                  <div className="er-quote">“…{item.karat}K {item.itemType}, {item.totalWeight}g total{item.hasStones ? ", set with stones" : ", solid gold"}, ships from {item.location}…”</div>
                </div>
              )}
              {item.weightBasis === "image" && (
                <div className="evidence-row">
                  <div className="er-k"><I.spark size={12}/> Image-scale reasoning</div>
                  <div className="er-quote">Reference object (coin/ruler) detected in photo 2; chain link gauge and length used to approximate volume → mass at {item.karat}K density.</div>
                </div>
              )}
            </div>
          </div>

          {/* classification + seller */}
          <div className="d-sect">
            <h3>Classification &amp; seller</h3>
            <div className="kv-grid">
              <div className="kv"><span className="k">Item type</span><span className="v">{item.itemType}</span></div>
              <div className="kv"><span className="k">Primary metal</span><span className="v">{item.primaryMetal}</span></div>
              <div className="kv"><span className="k">Condition</span><span className="v">{conditionLabel(item.condition) || "unknown"}</span></div>
              <div className="kv"><span className="k">Coverage</span><span className="v">{item.coverage}</span></div>
              <div className="kv"><span className="k">Seller</span><span className="v" data-testid="detail-seller">{sellerLabel(item)}</span></div>
              <div className="kv"><span className="k">Feedback</span><span className="v" data-testid="detail-seller-feedback">{sellerFeedbackLabel(item)}</span></div>
            </div>
          </div>

          {/* feedback */}
          <div className="d-sect">
            <h3>Was this accurate?</h3>
            <FeedbackForm item={item} signedIn={signedIn} onComingSoon={onComingSoon}/>
          </div>

          {/* related */}
          {related && related.length > 0 && (
            <div className="d-sect">
              <h3>Related items</h3>
              <div className="related-row">
                {related.map(r => (
                  <div key={r.id} className="related-card" onClick={() => onOpen?.(r)}>
                    <div className="rc-thumb"><ThumbPh item={r}/></div>
                    <div className="rc-info">
                      <div className="rc-t">{r.title}</div>
                      <div className="rc-r" style={{color: r.ratio != null && r.ratio < 1 ? "var(--accent-deep)" : "var(--ink-2)"}}>{r.ratio != null ? `${r.ratio.toFixed(2)}×` : "unscored"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="detail-foot">
          <a className="btn primary" href={item.listingUrl} target="_blank" rel="noreferrer" style={{flex:1, justifyContent:"center"}}>
            <I.ext size={14}/> Open original eBay listing
          </a>
          <button className="btn accent" onClick={(e)=>onSave?.(item, e)}>
            <I.star size={14}/> Save
          </button>
        </div>
      </div>
    </>
  );
};

export { DetailDrawer, ReasonCode, STATUS_META };
